// shared-lib/cabinet-runs/glassUppers.js
//
// Glass doors on an upper cabinet (plan 2026-10-21, Phase 2, C11–C16). Glass
// is not a front: a bay's `glass` (or the run's `upperSpec.glass`) draws the
// box in the `glass-front` STYLE after the appearance pass, so the run's look
// still decides the cabinet's design and colour — a glass Houston upper is a
// Houston cabinet in its colour (`familyOffering.designForLook`), and a Venus
// one is priced as its W box plus the GDW door made for it
// (`vendors/venus/resolveSku`). The one rule for "may this box have glass
// doors, and if not, why" — the fill, the write boundary and the panels ask
// it. Pure.

const { familyOf } = require('./families');
const {
  offeringOf,
  designForLook,
  designSellsGlass,
} = require('./familyOffering');
const { familySizes, resolveCabinetSku, unitsOf } = require('./vendorSizes');
const { resolveDoorStyle } = require('./doorStyles');

const GLASS_FRONT = 'glass-front';
/** The one sentence a run that already draws Glass Front is locked by (C14). */
const GLASS_FRONT_LOCK = "This run's door style is Glass Front";

/** A height in inches as a sentence says it. */
const said = (n) => `${+n.toFixed(2)}"`;
/** "a", "a or b", "a, b or c". */
function listSaid(values) {
  const s = values.map(said);
  if (s.length <= 1) return s.join('');
  return `${s.slice(0, -1).join(', ')} or ${s[s.length - 1]}`;
}

/**
 * Draw the glass doors the switch asked for over a built cabinet's look
 * (`fillRun`, after the appearance bundle). The look's own door style is kept
 * as `glassOver`, so a writer that restyles the cabinet (a recolour, a family
 * switch, a style tool) changes the look UNDER the glass instead of erasing
 * the glass or writing Glass Front into the run's look (`keepGlass`).
 * Mutates, like the appearance pass.
 */
function drawGlassDoors(cabinet) {
  cabinet.glassOver = resolveDoorStyle(cabinet.doorStyle);
  cabinet.doorStyle = GLASS_FRONT;
}

/** An object's LOOK door style — under glass the switch drew, the look's own. */
const lookDoorStyleOf = (o) =>
  o && o.glassOver != null ? o.glassOver : o?.doorStyle;

/**
 * `after` (an object a writer restyled from `before`) with the glass the
 * switch drew on `before` kept: a new door style goes UNDER the glass. An
 * object the switch did not glass comes back as it is.
 */
function keepGlass(before, after) {
  if (before?.glassOver == null || !after) return after;
  const style =
    after.doorStyle && after.doorStyle !== GLASS_FRONT
      ? after.doorStyle
      : before.glassOver;
  return { ...after, glassOver: style, doorStyle: GLASS_FRONT };
}

/**
 * The widths (inches) `family` makes a glass door for a plain wall cabinet in,
 * ascending — null when any width can take one (a family with no size
 * catalog). THE rule for "which widths can a glass run's uppers be" (user
 * ruling 2026-10-21, plan C13 → b): the fill reads it through
 * `glassFillOpts`, and nothing names a vendor.
 */
function glazableWidthsIn(family) {
  if (!familySizes(family)) return null;
  return [
    ...new Set(
      unitsOf(family, 'glass-door')
        .filter((u) => /^W\d/.test(u.fits))
        .map((u) => u.w)
    ),
  ].sort((a, b) => a - b);
}

/**
 * The upper tier's fill options for `run`: when the run asks for glass
 * (`upperSpec.glass`), only the widths its line can glaze, so every upper it
 * packs can be glass (C13). A run that asks for none — and a family that
 * glazes any width — packs exactly as it always has.
 */
function glassFillOpts(run, fillOpts) {
  if (run?.upperSpec?.glass !== true) return fillOpts;
  return glazableFillOpts(familyOf(run), fillOpts);
}

/** `fillOpts` narrowed to the widths `family` makes a glass door for — a
 * family that glazes any width is left as it is. Shared by the uppers
 * (`glassFillOpts`) and a glass stacked row (`upperStack.js`). */
function glazableFillOpts(family, fillOpts) {
  const glazable = glazableWidthsIn(family);
  if (!glazable) return fillOpts;
  const allowed = (fillOpts?.allowedWidthsIn || glazable).filter((w) =>
    glazable.includes(w)
  );
  return { ...fillOpts, allowedWidthsIn: allowed };
}

/** Does this run (or this bay, which says first) ask for glass doors? */
function wantsGlass(run, override) {
  if (override && override.glass != null) return override.glass === true;
  return run?.upperSpec?.glass === true;
}

/** A corner wall cabinet — the L's unit or a blind one. No glass (C11). */
const isCornerUpper = (box) =>
  box?.type === 'corner-cabinet' || !!box?.category?.blindCorner;

const vendorOf = (family) => family?.vendor || family?.name || 'This line';

/**
 * The sizes a vendor makes glass doors for plain wall cabinets in, as the
 * sentence a closed size says: "… 12" to 36" wide, 15", 18" … tall".
 */
function glassSizesSentence(family) {
  const doors = unitsOf(family, 'glass-door').filter(
    (u) => /^W\d/.test(u.fits) && !u.fits.startsWith('WDC')
  );
  const widths = [...new Set(doors.map((u) => u.w))].sort((a, b) => a - b);
  const heights = [...new Set(doors.map((u) => u.h))].sort((a, b) => a - b);
  if (!widths.length) return `${vendorOf(family)} makes no glass doors`;
  return `${vendorOf(family)} makes glass doors for wall cabinets ${said(
    widths[0]
  )} to ${said(widths[widths.length - 1])} wide, ${listSaid(heights)} tall`;
}

/**
 * Why `box` (an upper as built, or a probe) may not have glass doors in
 * `run`, wearing `look` (the appearance the run gives it — its design and
 * colour), or null when it may:
 *  - a corner wall cabinet never does (C11);
 *  - a run already in the Glass Front look is locked to it (C14);
 *  - a family that lists its door designs sells glass only on the designs
 *    flagged for it (C12) — none at all, or not this one;
 *  - a vendor with a size catalog makes a glass door only for some boxes.
 */
function glassUnavailableReason(run, box, look) {
  const family = familyOf(run);
  const catalog = !!familySizes(family);
  if (isCornerUpper(box))
    return catalog
      ? `${vendorOf(family)} makes no glass door for a corner wall cabinet`
      : 'Corner wall cabinets take no glass doors';
  const runWide = runGlassUnavailableReason(run, look);
  if (runWide) return runWide;
  if (!catalog) return null;
  const glass = resolveCabinetSku(family, { ...box, doorStyle: GLASS_FRONT });
  return glass.sku && glass.glassDoor?.sku ? null : glassSizesSentence(family);
}

/**
 * Why NO upper of `run`, in `look`, may have glass doors — the run's look is
 * already Glass Front (C14), its family sells no glass, or not in this design
 * (C12) — or null. What the run panel's "Glass uppers" asks; a box's own
 * reasons (a corner, its size) are `glassUnavailableReason`'s.
 */
function runGlassUnavailableReason(run, look) {
  const family = familyOf(run);
  if (resolveDoorStyle(look?.doorStyle) === GLASS_FRONT)
    return GLASS_FRONT_LOCK;
  const offering = offeringOf(family);
  if (!offering.restricts.door) return null;
  if (!offering.doorDesigns.some((d) => designSellsGlass(family, d)))
    return `${family.name} sells no glass doors`;
  const design = designForLook(offering, look || {});
  if (design && !designSellsGlass(family, design))
    return `${vendorOf(family)} makes no glass doors in the ${design.name} design`;
  return null;
}

/**
 * The caption under a glass toggle that is on offer, for a family with a
 * size catalog: "Venus Cabinetry makes glass doors 15", 18", 30", 36" or 42"
 * tall." Null for any other family (it makes every size).
 */
function glassHeightsCaption(run) {
  const family = familyOf(run);
  if (!familySizes(family)) return null;
  const heights = [
    ...new Set(unitsOf(family, 'glass-door').map((u) => u.h)),
  ].sort((a, b) => a - b);
  return heights.length
    ? `${vendorOf(family)} makes glass doors ${listSaid(heights)} tall.`
    : null;
}

/** Is `reason` the size sentence — the box, not the run's look, says no? */
const isSizeReason = (family, reason) =>
  !!family && reason === glassSizesSentence(family);

/**
 * The one warning for the run's switch: "2 uppers stay solid: Venus
 * Cabinetry makes no glass door for them" — or, when the boxes stay solid for
 * a reason that is the run's (its design), that reason.
 */
function solidUppersWarning(run, reasons, [one, many] = ['upper', 'uppers']) {
  if (!reasons.length) return null;
  const family = familyOf(run);
  const n = reasons.length;
  const why = reasons.every((r) => isSizeReason(family, r))
    ? `${vendorOf(family)} makes no glass door for ${n === 1 ? 'it' : 'them'}`
    : reasons[0];
  return {
    severity: 'warning',
    type: 'upper-glass',
    message: `${n === 1 ? `1 ${one} stays` : `${n} ${many} stay`} solid: ${why}`,
  };
}

/** A refusal the run's switch says nothing about: a corner (C11), or a run
 * already drawn Glass Front (C14). */
const isSilentGlassRefusal = (box, reason) =>
  isCornerUpper(box) || reason === GLASS_FRONT_LOCK;

module.exports = {
  GLASS_FRONT,
  GLASS_FRONT_LOCK,
  drawGlassDoors,
  lookDoorStyleOf,
  keepGlass,
  wantsGlass,
  glazableWidthsIn,
  glassFillOpts,
  glazableFillOpts,
  glassUnavailableReason,
  runGlassUnavailableReason,
  glassHeightsCaption,
  glassSizesSentence,
  solidUppersWarning,
  isSilentGlassRefusal,
  isCornerUpper,
  vendorOf,
};
